import Moment from 'moment-timezone';
import { useMemo } from 'react';

import SelectInput from 'components/library/inputs/SelectInput';
import ListItem from 'components/library/data-display/ListItem';
import { useInterviewBatches } from 'hooks/queries/interview-batches';
import { formatDateRange } from 'libraries/formatters';

import type { ActionMeta, OnChangeValue } from 'react-select/dist/declarations/src/types';
import type { Option } from './SelectInput/types';

interface Props {
  className?: string;
  helperText?: string;
  includePastBatches?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  stageInterviewId?: string;
  onChange: (newValue: OnChangeValue<Option<string>, false>, actionMeta: ActionMeta<Option<string>>) => void;
  value?: string;
}

const InterviewBatchSelectInput = ({
  className,
  helperText,
  includePastBatches = false,
  isDisabled = false,
  isRequired = false,
  label,
  stageInterviewId,
  onChange,
  value,
}: Props) => {
  const interviewBatchesQuery = useInterviewBatches({ stage_interview_id: stageInterviewId });

  const interviewBatches = useMemo(() => {
    if (!interviewBatchesQuery.data) {
      return [];
    }
    if (includePastBatches) {
      return interviewBatchesQuery.data.interview_batches;
    }
    return interviewBatchesQuery.data.interview_batches.filter((batch) => {
      const now = Moment();
      return Moment(batch.latest_interview_date).isAfter(now, 'day');
    });
  }, [interviewBatchesQuery.data]);

  const options = useMemo<Option<string>[]>(() => {
    const batchOptions: Option<string>[] = interviewBatches.map((batch) => ({
      value: batch.id,
      label: batch.name,
      secondaryText: `Times on ${formatDateRange(batch.earliest_interview_date, batch.latest_interview_date)}`,
    }));
    return batchOptions;
  }, [interviewBatches]);

  return (
    <SelectInput
      className={className}
      formatOptionLabel={(option, { context }) => (
        context === 'menu' ?
          <ListItem
            label={option.label}
            secondaryText={option.secondaryText}
          /> :
          option.label
      )}
      helperText={helperText}
      isClearable
      isDisabled={isDisabled}
      isRequired={isRequired}
      label={label}
      onChange={onChange}
      options={options}
      placeholder=""
      value={value ? options.find((option) => option.value === value) : null}
    />
  );
};

export default InterviewBatchSelectInput;
